<template>
  <ModalDialog
    :header-text="props.product.LabelName"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div class="product-details-container">
        <Grid
          :data-items="productProperties"
          :columns="columns"
          :scrollable="'none'"
          data-cy="schedule-b-details-grid"
        >
          <template v-slot:emptyHeader="{}"> </template>
        </Grid>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { EnumDpm } from "@/Domain/EnumDpm";
import type { ProductDisplay } from "@/Domain/ProductDisplay";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import { ref } from "vue";
import { getConsumerIndustrialString } from "@/shared/helpers/productsHelper";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { EnumGroup } from "@/Domain/EnumGroup";
import { EnumSealSign } from "@/Domain/EnumSealSign";
import { EnumConfidential } from "@/Domain/EnumConfidential";
import { convertNumber } from "@/Domain/EnumGroupConverter";

const props = defineProps<{
  product: ProductDisplay;
}>();

const emit = defineEmits(["closeModal"]);

const productProperties = ref<{ name: string; value: string }[]>([]);

const columns: GridColumnProps[] = [
  {
    field: "name",
    headerCell: "emptyHeader",
    className: "name-row",
  },
  {
    field: "value",
    headerCell: "emptyHeader",
  },
];

const setProductProperties = function () {
  productProperties.value = [
    { name: "Label Number", value: props.product.LabelNumber },
    {
      name: "Brand Name",
      value: props.product.BrandName,
    },
    {
      name: "Group",
      value: props.product._Group ? convertNumber(props.product._Group): "",
    },
    { name: "Symbol", value: props.product.Symbol },
    {
      name: "DPM",
      value: props.product._Dpm ? EnumDpm[props.product._Dpm] : "",
    },
    {
      name: "Passover Always",
      value: props.product._Pesach ? "Y" : "N",
    },
    {
      name: "UKD ID",
      value: props.product.VueFullUkdIdWithSuppressUkdId,
    },
    {
      name: "Product Status",
      value: props.product.CertificationStatus,
    },
  ]
 
  if (props.product.MerchLabelComment.trim().length > 0)
    productProperties.value.push({
      name: "Stipulation",
      value: props.product.MerchLabelComment,
    });
  if (props.product.MerchLabelScheduleBComment?.trim().length > 0)
    productProperties.value.push({
      name: "Schedule B Comment",
      value: props.product.MerchLabelScheduleBComment,
    });
  if (props.product.SealSignComment)
    productProperties.value.push({
      name: "Seal or Signature Required",
      value: props.product.SealSignComment,
    });

  productProperties.value.push({
    name: "Plant Name",
    value: props.product.PlantName,
  });

  productProperties.value.push({
    name: "Confidential",
    value:
      props.product._Confidential && props.product._Confidential !== 1
        ? EnumConfidential[props.product._Confidential]
        : "N",
  });

  productProperties.value.push({
    name: "Consumer/Industrial",
    value: getConsumerIndustrialString(props.product),
  });

  if (props.product.LocHold)
    productProperties.value.push({
      name: "LOC Hold",
      value: props.product.LocHold,
    });

  if (props.product.IsRepack)
    productProperties.value.push({
      name: "Repack",
      value: props.product.IsRepack,
    });
};

setProductProperties();

const closeModal = function () {
  emit("closeModal");
};
</script>

<style>
.product-details-container .k-grid-header {
  display: none !important;
}
</style>
